import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Paintbrush, Shield, Clock, Leaf, Gem, Heart } from 'lucide-react';

const features = [
  {
    icon: Paintbrush,
    title: "100% Handcrafted",
    description: "Every stroke is made by hand with traditional calligraphy techniques, ensuring each piece is unique."
  },
  {
    icon: Gem,
    title: "Premium Materials",
    description: "We use only the finest canvas, gold leaf, and archival-quality paints for lasting beauty."
  },
  {
    icon: Heart,
    title: "Made with Devotion",
    description: "Each artwork is created with spiritual intention, carrying blessings into your home."
  },
  {
    icon: Shield,
    title: "Authenticity Guaranteed",
    description: "Every piece comes with a certificate of authenticity and artist's signature."
  },
  {
    icon: Clock,
    title: "Timeless Art",
    description: "Our artworks are designed to be passed down through generations as family heirlooms."
  },
  {
    icon: Leaf,
    title: "Eco-Friendly",
    description: "Sustainable materials and processes that respect both art and environment."
  }
];

const FeatureCard = ({ feature, index, isInView }) => {
  return (
    <motion.div
      className="feature-card group"
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      data-testid={`feature-card-${index}`}
    >
      {/* Icon */}
      <div className="w-14 h-14 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center mb-5 group-hover:bg-[#D4AF37]/20 transition-colors">
        <feature.icon className="text-[#D4AF37]" size={26} />
      </div>

      {/* Content */}
      <h3 className="font-cinzel text-xl text-white mb-3 group-hover:text-[#D4AF37] transition-colors">
        {feature.title}
      </h3>
      <p className="font-manrope text-gray-400 text-sm leading-relaxed">
        {feature.description}
      </p>
    </motion.div>
  );
};

const WhyChooseSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  return (
    <section className="py-24 relative overflow-hidden" ref={sectionRef} data-testid="why-choose-section">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-[#D4AF37] blur-[150px]" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full bg-[#D4AF37] blur-[150px]" />
      </div>

      <div className="section-container relative z-10">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <span className="font-manrope text-sm text-[#D4AF37] uppercase tracking-widest mb-4 block">
            Our Promise
          </span>
          <h2 className="font-cinzel text-section-title text-gold-gradient mb-4">
            Why Choose Us
          </h2>
          <div className="gold-line mx-auto mb-6" />
          <p className="font-manrope text-gray-400 max-w-2xl mx-auto">
            We don't just create art—we craft spiritual experiences that transform your living spaces into sanctuaries of peace and beauty.
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <FeatureCard
              key={index}
              feature={feature}
              index={index}
              isInView={isInView}
            />
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.8 }}
        >
          <p className="font-amiri text-2xl text-[#D4AF37]/80 italic">
            "Art is a reflection of the soul"
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyChooseSection;
